import { useEffect, useState } from "react";
import UnlimitTable from "../../../../components/ui-kit/unlimit-table";
import httpService from "../../../../services/http/inventory/outlet-stock.service";
import usePagination, {
  createInitialPagination,
} from "../../../../custom-hooks/usePagination";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Plus, Search, X } from "react-feather";
import ShopPrdocutsSelectionModal from "./ShopPrdocutsSelectionModal";
import ConfirmationAlert from "../../../../components/confirm-alert";
import notificationService from "../../../../services/toaster.service";

export default function OutletsDetailsStock({ id }) {
  const [loading, setLoading] = useState(false);
  const [outletStocks, setOutletStocks] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { t } = useTranslation();

  // Search filters
  const [nameFilter, setNameFilter] = useState("");
  const [lmProductNoFilter, setLmProductNoFilter] = useState("");

  const initialPagination = createInitialPagination(t);
  const { paginationParams, updatePaginationParams } =
    usePagination(initialPagination);

  const getOutletStockList = async (
    pagination = paginationParams.pagination,
    filters,
    sorter = {}
  ) => {
    const data = {
      page: pagination?.current,
      per_page: pagination?.pageSize,
      ...filters,
      group_by: "ean",
      ...sorter,
    };
    setLoading(true);
    await httpService
      .shopProducts(id, data)
      .then((response) => {
        if (response.status == 200) {
          setOutletStocks(response.data.data?.data);
        }
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
            current: response.data.data.current_page,
            total: response.data.data.total,
            pageSize: response.data.data.per_page,
          },
        });
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    filters = {
      name: nameFilter,
      lm_product_number: lmProductNoFilter, // Added LM Product No to filters
      outlet_id: id,
    };
    const nonNullFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => !!value)
    );
    if (sorter && sorter.field) {
      sorter = {
        sort_by: sorter.field,
        sort_direction: sorter.order == "ascend" ? "ASC" : "DESC",
      };
    }
    if (!pagination) {
      pagination = initialPagination;
    }
    await updatePaginationParams({
      pagination,
      sorter,
    });
    await getOutletStockList(pagination, nonNullFilters, sorter);
  };

  // Clear search filters
  const clearSearch = () => {
    setNameFilter("");
    setLmProductNoFilter("");
    // Trigger search with empty filters
    setTimeout(() => {
      handleTableChange();
    }, 0);
  };

  // Handle search on enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleTableChange();
    }
  };

  // Check if any filter has value
  const hasActiveFilters = nameFilter || lmProductNoFilter;

  const handleAddProducts = async (selectedProducts) => {
    try {
      // Prepare the data for API call
      const productsToAdd = selectedProducts.map((product) => ({
        product_id: product.id,
        quantity: product.quantity,
        outlet_id: id,
        // Add other required fields based on your API requirements
      }));

      // Call your API to add products to outlet stock
      // Replace this with your actual API call
      const response = await httpService.addProductsToOutlet({
        outlet_id: id,
        products: productsToAdd,
      });

      if (response.status === 200) {
        notificationService.successMessage(
          t("outlet.productsAddedSuccessfully")
        );
        // Refresh the stock list
        getOutletStockList();
      }
    } catch (error) {
      console.error("Error adding products:", error);
      notificationService.errorMessage(t("outlet.errorAddingProducts"));
    }
  };

  const onConfirmDelete = async (productId) => {
    try {
      const response = await httpService.deleteShopProduct(id, productId);
      if (response.status === 200) {
        notificationService.successMessage(
          t("shop.productDeletedSuccessfully")
        );
        getOutletStockList();
      }
    } catch (error) {
      console.error("Error deleting stock:", error);
      notificationService.errorMessage(t("outlet.errorDeletingStock"));
    }
  };

  useEffect(() => {
    getOutletStockList();
  }, []);

  const onCloseModal = () => {
    setModalIsOpen(false);
    getOutletStockList();
  };

  const columns = [
    {
      title: t("tableColumnName.srNo"),
      dataIndex: "index",
      render: (value, item, index) =>
        (paginationParams?.pagination?.current - 1) *
          paginationParams?.pagination?.pageSize +
        (index + 1),
    },
    {
      title: t("inputField.label.EAN"),
      dataIndex: "ean",
      render: (text, record) =>
        record.catalog ? record?.catalog?.ean : record?.ean,
    },
    {
      title: t("tableColumnName.lmProductNo"),
      dataIndex: "lm_product_number",
      render: (text, record) =>
        record?.lm_product_number || record?.catalog?.lm_product_number,
    },
    {
      title: t("tableColumnName.productName"),
      dataIndex: "name",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <ImageWithBasePath
            type="link"
            src={
              record?.catalog?.main_product_image || record?.main_product_image
            }
            alt={t("tableColumnName.productName")}
            style={{
              width: 30,
              height: 30,
              marginRight: 10,
              objectFit: "cover",
            }}
          />
          {record?.catalog?.name || record?.name}
        </div>
      ),
    },
    {
      title: t("tableColumnName.sku"),
      dataIndex: "sku",
      render: (text, record) => record?.sku || record?.catalog?.sku,
    },
    {
      title: t("tableColumnName.actions"),
      key: "actions",
      align: "center",
      render: (text, record) => (
        <div className="action-table-data" style={{ minWidth: "140px" }}>
          <div className="edit-delete-action">
            <ConfirmationAlert onConfirm={() => onConfirmDelete(record.id)} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="table-top row mb-3">
        <div className="d-flex justify-content-between align-items-center gap-2">
          <div className="d-flex gap-2 justify-content-start">
            <div className="col-lg-6 ">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="LM Product No"
                  className="form-control form-control-sm formsearch-custom ps-2"
                  value={lmProductNoFilter}
                  onChange={(e) => setLmProductNoFilter(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                {lmProductNoFilter && (
                  <button
                    type="button"
                    className="btn btn-sm position-absolute"
                    style={{
                      right: "5px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                      padding: "2px",
                    }}
                    onClick={() => {
                      setLmProductNoFilter("");
                      setTimeout(() => handleTableChange(), 0);
                    }}
                    title="Clear LM Product No"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="col-lg-6 ">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder={t("tableColumnName.productName")}
                  className="form-control form-control-sm formsearch-custom ps-2"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                {nameFilter && (
                  <button
                    type="button"
                    className="btn btn-sm position-absolute"
                    style={{
                      right: "5px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                      padding: "2px",
                    }}
                    onClick={() => {
                      setNameFilter("");
                      setTimeout(() => handleTableChange(), 0);
                    }}
                    title="Clear Product Name"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => handleTableChange()}
              disabled={loading}
              title="Search"
            >
              <Search size={16} />
            </button>
          </div>

          {/* Search and Clear Buttons */}
          <div className="d-flex gap-2">
            <button
              className="btn btn-added"
              onClick={() => setModalIsOpen(true)}
            >
              <Plus className="me-2 iconsize" />
              {t("general.button.addNew")}
            </button>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mb-3">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <small className="text-muted">Active filters:</small>
            {lmProductNoFilter && (
              <span className="badge bg-light text-dark border">
                LM Product: {lmProductNoFilter}
                <button
                  type="button"
                  className="btn-close btn-close-sm ms-1"
                  style={{ fontSize: "8px" }}
                  onClick={() => {
                    setLmProductNoFilter("");
                    setTimeout(() => handleTableChange(), 0);
                  }}
                  aria-label="Remove LM Product filter"
                ></button>
              </span>
            )}
            {nameFilter && (
              <span className="badge bg-light text-dark border">
                Name: {nameFilter}
                <button
                  type="button"
                  className="btn-close btn-close-sm ms-1"
                  style={{ fontSize: "8px" }}
                  onClick={() => {
                    setNameFilter("");
                    setTimeout(() => handleTableChange(), 0);
                  }}
                  aria-label="Remove Name filter"
                ></button>
              </span>
            )}
          </div>
        </div>
      )}

      <div className="table-responsive">
        <UnlimitTable
          pagination={paginationParams.pagination}
          columns={columns}
          dataSource={outletStocks}
          rowKey={(record) => record.id}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>

      {modalIsOpen && (
        <ShopPrdocutsSelectionModal
          isOpen={modalIsOpen}
          closeModal={onCloseModal}
          onAddProducts={handleAddProducts}
          outletId={id}
        />
      )}
    </>
  );
}
